import { useEffect, useState, useCallback } from 'react';
import {
  MagnifyingGlass, FolderOpen, Tag, Plus, Trash, PencilSimple, X,
  ArrowsClockwise, FloppyDisk, Translate, Sparkle, Warning, CheckCircle,
  CaretDown, CaretRight,
} from '@phosphor-icons/react';
import { useProfile } from '../context/ProfileContext';

const API = process.env.REACT_APP_BACKEND_URL;

const COUNTRY_OPTIONS = [
  { code: 'NL', label: '🇳🇱 NL (master)' },
  { code: 'BE', label: '🇧🇪 BE' }, { code: 'DE', label: '🇩🇪 DE' },
  { code: 'FR', label: '🇫🇷 FR' }, { code: 'GB', label: '🇬🇧 GB' },
  { code: 'IE', label: '🇮🇪 IE' }, { code: 'AT', label: '🇦🇹 AT' },
  { code: 'CH', label: '🇨🇭 CH' }, { code: 'LU', label: '🇱🇺 LU' },
  { code: 'ES', label: '🇪🇸 ES' }, { code: 'PT', label: '🇵🇹 PT' },
  { code: 'IT', label: '🇮🇹 IT' }, { code: 'DK', label: '🇩🇰 DK' },
  { code: 'SE', label: '🇸🇪 SE' }, { code: 'NO', label: '🇳🇴 NO' },
  { code: 'FI', label: '🇫🇮 FI' }, { code: 'PL', label: '🇵🇱 PL' },
];

export default function SearchTermsV2Page() {
  const { active: activeProfile } = useProfile();
  const [country, setCountry] = useState('NL');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [editingItem, setEditingItem] = useState(null);  // {kind, id}
  const [draftValue, setDraftValue] = useState('');
  const [marketDescription, setMarketDescription] = useState('');
  const [descSaving, setDescSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [profileMeta, setProfileMeta] = useState(null); // { slug, name }

  const load = useCallback(async () => {
    setLoading(true);
    // Retry up to 3x to overcome transient 520/502 errors from Cloudflare/ingress.
    let lastErr = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const r = await fetch(`${API}/api/search-terms-v2?country=${country}`, { credentials: 'include' });
        if (!r.ok) {
          // 5xx → retry with backoff
          if (r.status >= 500 && attempt < 3) {
            await new Promise(res => setTimeout(res, 800 * attempt));
            continue;
          }
          throw new Error(`Laden mislukt (HTTP ${r.status})`);
        }
        const d = await r.json();
        setData(d);
        setError(null);
        setLoading(false);
        return;
      } catch (e) {
        lastErr = e;
        if (attempt < 3) {
          await new Promise(res => setTimeout(res, 800 * attempt));
        }
      }
    }
    setError(lastErr?.message || 'Laden mislukt');
    setLoading(false);
  }, [country]);

  // Load profile's market description
  const loadProfile = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/profiles`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      const active = (d.profiles || []).find(p => p.slug === d.active) || (d.profiles || [])[0];
      if (active) {
        setProfileMeta({ slug: active.slug, name: active.name });
        setMarketDescription(active.market_description || '');
      }
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); loadProfile(); }, [load, loadProfile, activeProfile]);

  const isMaster = country === 'NL';
  const groups = data?.groups || [];

  // Save full structure (only for NL master)
  const save = async (newGroups) => {
    if (!isMaster) return;
    setSaving(true); setError(null); setSuccess(null);
    try {
      const r = await fetch(`${API}/api/search-terms-v2`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groups: newGroups }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Opslaan mislukt');
      }
      const d = await r.json();
      setData((prev) => ({ ...prev, groups: d.groups || newGroups }));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Group operations
  const addGroup = async () => {
    const name = window.prompt('Naam nieuwe groep:');
    if (!name?.trim()) return;
    const newGroups = [...groups, { id: `tmp_${Date.now()}`, name: name.trim(), order: groups.length, subgroups: [] }];
    await save(newGroups);
  };
  const renameGroup = async (gid, currentName) => {
    const name = window.prompt('Nieuwe naam:', currentName);
    if (!name?.trim() || name === currentName) return;
    await save(groups.map(g => g.id === gid ? { ...g, name: name.trim() } : g));
  };
  const deleteGroup = async (gid, name) => {
    if (!window.confirm(`Groep "${name}" en alle subgroepen verwijderen?`)) return;
    await save(groups.filter(g => g.id !== gid));
  };

  // Subgroup operations
  const addSubgroup = async (gid) => {
    const name = window.prompt('Naam nieuwe subgroep:');
    if (!name?.trim()) return;
    await save(groups.map(g => g.id === gid
      ? { ...g, subgroups: [...g.subgroups, { id: `tmp_${Date.now()}`, name: name.trim(), order: g.subgroups.length, terms: [], filters: [], must_contain: [] }] }
      : g));
  };
  const renameSubgroup = async (gid, sid, currentName) => {
    const name = window.prompt('Nieuwe naam:', currentName);
    if (!name?.trim() || name === currentName) return;
    await save(groups.map(g => g.id === gid
      ? { ...g, subgroups: g.subgroups.map(sg => sg.id === sid ? { ...sg, name: name.trim() } : sg) }
      : g));
  };
  const deleteSubgroup = async (gid, sid, name) => {
    if (!window.confirm(`Subgroep "${name}" verwijderen?`)) return;
    await save(groups.map(g => g.id === gid
      ? { ...g, subgroups: g.subgroups.filter(sg => sg.id !== sid) }
      : g));
  };

  // Term/filter operations (kind = 'terms' or 'filters')
  const addTerm = async (gid, sid, kind, value) => {
    if (!value?.trim()) return;
    await save(groups.map(g => g.id === gid
      ? { ...g, subgroups: g.subgroups.map(sg => sg.id === sid
          ? { ...sg, [kind]: [...(sg[kind] || []), value.trim()] }
          : sg) }
      : g));
  };
  const removeTerm = async (gid, sid, kind, idx) => {
    await save(groups.map(g => g.id === gid
      ? { ...g, subgroups: g.subgroups.map(sg => sg.id === sid
          ? { ...sg, [kind]: (sg[kind] || []).filter((_, i) => i !== idx) }
          : sg) }
      : g));
  };

  const saveDescription = async () => {
    if (!profileMeta?.slug) return;
    setDescSaving(true); setError(null); setSuccess(null);
    try {
      const r = await fetch(`${API}/api/profiles/${profileMeta.slug}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ market_description: marketDescription }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Opslaan mislukt');
      }
      setSuccess('Beschrijving opgeslagen');
    } catch (e) {
      setError(e.message);
    } finally {
      setDescSaving(false);
    }
  };

  const aiGenerate = async () => {
    if (!marketDescription || marketDescription.trim().length < 20) {
      setError('Vul eerst een beschrijving in (min. 20 tekens)');
      return;
    }
    if (groups.length > 0 && !window.confirm(`Profiel "${profileMeta?.name || ''}" heeft al ${groups.length} groepen. Overschrijven met AI-gegenereerde structuur?`)) return;
    setAiGenerating(true); setError(null); setSuccess(null);
    try {
      // First save description so it persists on the profile
      if (profileMeta?.slug) {
        await fetch(`${API}/api/profiles/${profileMeta.slug}`, {
          method: 'PATCH', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ market_description: marketDescription }),
        }).catch(() => {});
      }
      const r = await fetch(`${API}/api/search-terms-v2/ai-generate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: marketDescription, overwrite: groups.length > 0 }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'AI generatie mislukt');
      }
      const d = await r.json();
      setSuccess(`AI structuur geladen voor ${d.profile_name} — ${d.groups_count} groepen, ${d.total_terms} termen`);
      // Use returned data directly so we don't depend on a re-fetch (avoids 520 races).
      if (Array.isArray(d.groups)) {
        setData(prev => ({
          ...(prev || {}),
          profile_id: d.profile,
          is_master: true,
          country: 'NL',
          groups: d.groups,
          total_terms: d.total_terms,
        }));
      }
      // Best-effort re-fetch in background to sync filter dimensions etc.
      load();
      loadProfile();
    } catch (e) {
      setError(e.message);
    } finally {
      setAiGenerating(false);
    }
  };

  const translate = async () => {
    if (isMaster) return;
    setTranslating(country); setError(null); setSuccess(null);
    try {
      const r = await fetch(`${API}/api/search-terms-v2/translate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ country }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Vertaling mislukt');
      }
      const d = await r.json();
      setSuccess(`${d.translated_count} termen vertaald naar ${d.language}`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setTranslating(null);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="search-terms-v2-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900 flex items-center gap-2">
            <MagnifyingGlass size={24} weight="duotone" />
            Zoektermen
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Beheer per profiel je zoektermen in groepen en subgroepen.
            <strong className="text-zinc-700"> NL is master</strong> — andere landen kun je automatisch laten vertalen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="px-3 py-2 text-sm border border-zinc-300 rounded-sm bg-white"
            data-testid="country-select"
          >
            {COUNTRY_OPTIONS.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
          {!isMaster && (
            <button
              onClick={translate}
              disabled={translating === country}
              className="px-3 py-2 text-sm bg-blue-600 text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="translate-btn"
            >
              {translating === country ? <ArrowsClockwise size={14} className="animate-spin" /> : <Translate size={14} />}
              {translating === country ? 'Vertalen...' : 'Vertaal vanuit NL'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2 text-sm text-red-800">
          <Warning size={18} weight="fill" className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded flex items-start gap-2 text-sm text-emerald-800">
          <CheckCircle size={18} weight="fill" className="mt-0.5 flex-shrink-0" /> {success}
        </div>
      )}

      {/* Market description card — top-level per-profile */}
      {isMaster && (
        <div className="bg-white border border-zinc-200 rounded-sm p-4 mb-4" data-testid="market-description-card">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="duotone" className="text-amber-500" />
              <h3 className="text-sm font-semibold text-zinc-900">
                Marktbeschrijving — {profileMeta?.name || 'Profiel'}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={saveDescription}
                disabled={descSaving || !profileMeta?.slug}
                className="px-2.5 py-1 text-xs border border-zinc-300 text-zinc-700 rounded-sm hover:bg-zinc-50 disabled:opacity-50 flex items-center gap-1"
                data-testid="save-description-btn"
              >
                <FloppyDisk size={12} /> {descSaving ? 'Opslaan...' : 'Opslaan'}
              </button>
              <button
                onClick={aiGenerate}
                disabled={aiGenerating || !marketDescription || marketDescription.trim().length < 20}
                className="px-2.5 py-1 text-xs bg-amber-100 text-amber-900 border border-amber-300 rounded-sm hover:bg-amber-200 disabled:opacity-50 flex items-center gap-1"
                data-testid="ai-generate-btn"
              >
                {aiGenerating
                  ? <><ArrowsClockwise size={12} className="animate-spin" /> Genereren...</>
                  : <><Sparkle size={12} /> {profileMeta?.name || 'Profiel'} AI import</>}
              </button>
            </div>
          </div>
          <textarea
            value={marketDescription}
            onChange={(e) => setMarketDescription(e.target.value)}
            rows={4}
            placeholder="Beschrijf de markt waarin dit profiel actief is — segmenten, doelgroepen, professionals, producten. De AI gebruikt deze beschrijving om groepen, subgroepen en termen te genereren. Min. 20 tekens."
            className="w-full text-sm border border-zinc-200 rounded-sm p-2.5 resize-y focus:outline-none focus:border-zinc-400"
            data-testid="market-description-textarea"
          />
          <p className="text-xs text-zinc-500 mt-1">
            {marketDescription.length} tekens
            {marketDescription.length > 0 && marketDescription.length < 20 && (
              <span className="text-amber-600 ml-2">· Minimaal 20 tekens om AI te gebruiken</span>
            )}
          </p>
        </div>
      )}

      {/* Header bar with stats + actions */}
      <div className="bg-white border border-zinc-200 rounded-sm p-4 mb-4 flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-zinc-600">
          <strong>{groups.length}</strong> groepen ·
          <strong className="ml-1">{groups.reduce((a, g) => a + g.subgroups.length, 0)}</strong> subgroepen ·
          <strong className="ml-1">{data?.total_terms || 0}</strong> termen totaal
          {!isMaster && data?.translated_at && (
            <span className="ml-3 text-xs text-zinc-400">
              · Vertaald op {new Date(data.translated_at).toLocaleString('nl-NL')}
            </span>
          )}
          {!isMaster && !data?.translated_at && groups.length === 0 && (
            <span className="ml-3 text-xs text-amber-600">· Nog niet vertaald — klik "Vertaal vanuit NL"</span>
          )}
        </div>
        {isMaster && (
          <div className="flex items-center gap-2">
            <button
              onClick={addGroup}
              disabled={saving}
              className="px-3 py-1.5 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800 flex items-center gap-2"
              data-testid="add-group-btn"
            >
              <Plus size={14} /> Nieuwe groep
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="p-8 text-center text-zinc-400 text-sm flex items-center justify-center gap-2">
          <ArrowsClockwise size={16} className="animate-spin" /> Laden...
        </div>
      ) : groups.length === 0 ? (
        <div className="p-8 text-center bg-white border border-zinc-200 rounded-sm">
          <FolderOpen size={40} className="mx-auto text-zinc-300 mb-3" weight="duotone" />
          <h3 className="text-zinc-700 font-medium">Nog geen groepen</h3>
          <p className="text-sm text-zinc-500 mt-1">
            {isMaster
              ? 'Vul hierboven de marktbeschrijving in en klik op de AI-import knop, of maak handmatig een groep aan.'
              : 'Vertaal eerst vanuit NL om termen voor dit land te genereren.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(g => {
            const isExp = expanded[g.id] !== false;
            return (
              <div key={g.id} className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid={`group-${g.id}`}>
                <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-100 flex items-center gap-2">
                  <button onClick={() => setExpanded(p => ({ ...p, [g.id]: !isExp }))} className="text-zinc-500" data-testid={`expand-group-${g.id}`}>
                    {isExp ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  </button>
                  <FolderOpen size={16} weight="duotone" className="text-zinc-600" />
                  <h3 className="font-medium text-zinc-900 flex-1">{g.name}</h3>
                  <span className="text-xs text-zinc-400">
                    {g.subgroups.length} subgroepen · {g.subgroups.reduce((a, sg) => a + sg.terms.length + sg.filters.length, 0)} termen
                  </span>
                  {isMaster && (
                    <div className="flex items-center gap-1">
                      <button onClick={() => renameGroup(g.id, g.name)} className="p-1 text-zinc-500 hover:text-zinc-900" title="Hernoem"><PencilSimple size={14} /></button>
                      <button onClick={() => deleteGroup(g.id, g.name)} className="p-1 text-red-500 hover:text-red-700" title="Verwijder"><Trash size={14} /></button>
                    </div>
                  )}
                </div>

                {isExp && (
                  <div className="p-3 space-y-2">
                    {g.subgroups.map(sg => (
                      <div key={sg.id} className="border border-zinc-100 rounded-sm" data-testid={`subgroup-${sg.id}`}>
                        <div className="px-3 py-2 bg-zinc-50/50 border-b border-zinc-100 flex items-center gap-2">
                          <Tag size={14} className="text-zinc-500" />
                          <span className="text-sm font-medium text-zinc-800 flex-1">{sg.name}</span>
                          <span className="text-xs text-zinc-400">{sg.terms.length} termen + {sg.filters.length} filters</span>
                          {isMaster && (
                            <div className="flex items-center gap-1">
                              <button onClick={() => renameSubgroup(g.id, sg.id, sg.name)} className="p-1 text-zinc-400 hover:text-zinc-700" title="Hernoem"><PencilSimple size={12} /></button>
                              <button onClick={() => deleteSubgroup(g.id, sg.id, sg.name)} className="p-1 text-red-400 hover:text-red-600" title="Verwijder"><Trash size={12} /></button>
                            </div>
                          )}
                        </div>
                        <div className="p-3 space-y-2">
                          <TermsList kind="terms" label="Zoektermen" items={sg.terms} canEdit={isMaster}
                            onAdd={(v) => addTerm(g.id, sg.id, 'terms', v)}
                            onRemove={(i) => removeTerm(g.id, sg.id, 'terms', i)}
                          />
                          {(isMaster || sg.filters.length > 0) && (
                            <TermsList kind="filters" label="Extra filters" items={sg.filters} canEdit={isMaster}
                              onAdd={(v) => addTerm(g.id, sg.id, 'filters', v)}
                              onRemove={(i) => removeTerm(g.id, sg.id, 'filters', i)}
                            />
                          )}
                          {(isMaster || (sg.must_contain && sg.must_contain.length > 0)) && (
                            <TermsList
                              kind="must_contain"
                              label="Relevantie-vereiste (verplicht)"
                              hint="Faciliteit MOET minstens 1 van deze woorden bevatten in naam/website/snippet — anders wordt hij genegeerd vóór hij in MongoDB belandt."
                              items={sg.must_contain || []}
                              canEdit={isMaster}
                              onAdd={(v) => addTerm(g.id, sg.id, 'must_contain', v)}
                              onRemove={(i) => removeTerm(g.id, sg.id, 'must_contain', i)}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                    {isMaster && (
                      <button onClick={() => addSubgroup(g.id)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1" data-testid={`add-sub-${g.id}`}>
                        <Plus size={12} /> Subgroep toevoegen
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {saving && (
        <div className="fixed bottom-6 right-6 px-4 py-2 bg-zinc-900 text-white rounded-sm text-sm shadow-lg flex items-center gap-2">
          <ArrowsClockwise size={14} className="animate-spin" /> Opslaan...
        </div>
      )}
    </div>
  );
}

function TermsList({ kind, label, items, canEdit, onAdd, onRemove, hint }) {
  const [input, setInput] = useState('');
  const submit = () => { if (input.trim()) { onAdd(input); setInput(''); } };
  const isFilter = kind === 'filters';
  const isMust = kind === 'must_contain';
  const chipClass = isMust
    ? 'bg-rose-50 text-rose-900 border border-rose-300'
    : isFilter
      ? 'bg-amber-50 text-amber-900 border border-amber-200'
      : 'bg-zinc-100 text-zinc-800';
  const labelClass = isMust ? 'text-rose-700 font-semibold' : 'text-zinc-400';
  return (
    <div>
      <div className={`text-xs uppercase tracking-wider mb-1 ${labelClass}`}>{label}</div>
      {hint && (
        <div className="text-[11px] text-rose-700/70 italic mb-1.5 leading-snug">{hint}</div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {items.map((t, i) => (
          <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs ${chipClass}`} data-testid={`${kind}-chip`}>
            {t}
            {canEdit && (
              <button onClick={() => onRemove(i)} className="text-zinc-400 hover:text-red-600">
                <X size={10} weight="bold" />
              </button>
            )}
          </span>
        ))}
        {canEdit && (
          <span className="inline-flex items-center gap-1">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              placeholder={isMust ? '+ verplicht woord' : (isFilter ? '+ filter' : '+ term')}
              className="px-2 py-0.5 border border-zinc-200 rounded-sm text-xs w-32 focus:border-blue-400 focus:outline-none"
              data-testid={`add-${kind}-input`}
            />
            {input && (
              <button onClick={submit} className="text-blue-600 hover:text-blue-800"><Plus size={12} weight="bold" /></button>
            )}
          </span>
        )}
        {!canEdit && items.length === 0 && (
          <span className="text-xs text-zinc-400 italic">Geen — vertaal vanuit NL master</span>
        )}
      </div>
    </div>
  );
}
